/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Avatar,
	BackgroundImage,
	Box,
	Flex,
	Group,
	Progress,
	Stack,
	Text,
} from '@mantine/core';

// Component
import { Button } from '@components/atoms/Button';
import { NumericInput } from '@components/atoms/NumericInput';

// Styles
import { Tooltip } from '@components/atoms/Tooltip';
import { NOT_APPLICABLE } from '@config/constants/common';
import { getCurrencyUnit } from '@utils/currency-units';
import {
	formatCurrency,
	formatNumberWithSpaces,
} from '@utils/formats';
import {
	isNumber,
	truncateString,
} from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import classes from './CardProject.module.css';
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const CardProject = (props: {
	size: string;
	type: string;
	data: any;
	isAdded: boolean;
	disabled?: boolean;
	inactive?: boolean;
	disabledLabel?: string;
	tco2: number;
	onAdd: (_isAdded: boolean) => void;
	onChangeTco2: (_tco2: any) => void;
	handleShowMoreDetails?: () => void;
	containerWidth?: string;
	disableActions?: boolean;
}) => {
	const { t } = useTranslation();

	/* ******************************** HELPER ********************************** */
	const IS_SMALL = props.size === 'sm';
	const IS_EXTRA_SMALL = props.size === 'responsive';

	const getBody = () => {
		switch (props.type) {
			case 'view-measurability':
				return (
					<>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.region')}
							</Text>
							<Tooltip
								isActive={
									props.data.region
										? props.data.region.length > 23
										: false
								}
								label={
									props.data.region
										? props.data.region
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										props.data.region
											? props.data.region
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.projectType')}
							</Text>
							<Tooltip
								isActive={
									t(props.data.type)
										? t(props.data.type).length > 23
										: false
								}
								label={
									t(props.data.type)
										? t(props.data.type)
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										t(props.data.type)
											? t(props.data.type)
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.sellPrice')}
							</Text>
							<Tooltip
								isActive={
									props.data.priceTco2
										? props.data.priceTco2.toString()
												.length > 23
										: false
								}
								label={
									props.data.priceTco2
										? props.data.priceTco2
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										props.data.priceTco2
											? props.data.priceTco2.toString()
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.availableStock')}
							</Text>
							<Tooltip
								isActive={
									props.data.priceTco2
										? props.data.priceTco2.toString()
												.length > 23
										: false
								}
								label={
									props.data.priceTco2
										? props.data.priceTco2
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										props.data.tokenAvailable
											? formatNumberWithSpaces(
													Number(
														props.data.tokenAvailable.toString()
													)
												)
											: '',
										23
									) +
										' ' +
										getUnitLabel('tco2e')}
								</Text>
							</Tooltip>
						</Box>
					</>
				);
			case 'view-additionality':
				return (
					<>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.region')}
							</Text>
							<Tooltip
								isActive={props.data.region.length > 23}
								label={props.data.region}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(props.data.region, 23)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.projectType')}
							</Text>
							<Tooltip
								isActive={t(props.data.type).length > 23}
								label={t(props.data.type)}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										t(props.data.type)
											? t(props.data.type)
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.financialNeeds')}
							</Text>
							<Tooltip
								isActive={
									props.data &&
									props.data.financialNeeds &&
									props.data.financialNeeds.length > 23
								}
								label={props.data.financialNeeds}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{props.data.financialNeeds ===
									'Unlimited'
										? t('global.unlimited')
										: isNumber(props.data.financialNeeds)
											? formatCurrency(
													Number(
														props.data.financialNeeds
													),
													getCurrencyUnit('EUR')
												)
											: NOT_APPLICABLE}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.coverage')}
							</Text>
							<Tooltip
								isActive={
									props.data &&
									props.data.coverage &&
									props.data.coverage.length > 23
								}
								label={props.data.coverage}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{formatCurrency(
										Number(props.data.coverage),
										getCurrencyUnit('EUR')
									)}
								</Text>
							</Tooltip>
						</Box>

						<Progress
							radius={'0px'}
							size={
								IS_SMALL || IS_EXTRA_SMALL ? 'sm' : 'md'
							}
							value={props.data.progress}
						/>
					</>
				);
			case 'measurability':
				return (
					<>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.region')}
							</Text>
							<Tooltip
								isActive={
									props.data.region
										? props.data.region.length > 23
										: false
								}
								label={
									props.data.region
										? props.data.region
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										props.data.region
											? props.data.region
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.projectType')}
							</Text>
							<Tooltip
								isActive={
									t(props.data.type)
										? t(props.data.type).length > 23
										: false
								}
								label={
									t(props.data.type)
										? t(props.data.type)
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										t(props.data.type)
											? t(props.data.type)
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.sellPrice')}
							</Text>
							<Tooltip
								isActive={
									props.data.priceTco2
										? props.data.priceTco2.toString()
												.length > 23
										: false
								}
								label={
									props.data.priceTco2
										? props.data.priceTco2
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										props.data.priceTco2
											? props.data.priceTco2.toString()
											: '',
										23
									)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.availableStock')}
							</Text>
							<Tooltip
								isActive={
									props.data.priceTco2
										? props.data.priceTco2.toString()
												.length > 23
										: false
								}
								label={
									props.data.priceTco2
										? props.data.priceTco2
										: ''
								}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(
										props.data.tokenAvailable
											? formatNumberWithSpaces(
													Number(
														props.data.tokenAvailable.toString()
													)
												)
											: '',
										23
									) +
										' ' +
										getUnitLabel('tco2e')}
								</Text>
							</Tooltip>
						</Box>
					</>
				);
			case 'additionality':
				return (
					<>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.region')}
							</Text>
							<Tooltip
								isActive={props.data.region.length > 23}
								label={props.data.region}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm
									)}
								>
									{truncateString(props.data.region, 23)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.projectType')}
							</Text>
							<Tooltip
								isActive={t(props.data.type).length > 23}
								label={t(props.data.type)}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{truncateString(t(props.data.type), 23)}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.financialNeeds')}
							</Text>
							<Tooltip
								isActive={
									props.data &&
									props.data.financialNeeds &&
									props.data.financialNeeds.length > 23
								}
								label={props.data.financialNeeds}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{props.data.financialNeeds ===
									'Unlimited'
										? t('global.unlimited')
										: isNumber(props.data.financialNeeds)
											? formatCurrency(
													Number(
														props.data.financialNeeds
													),
													getCurrencyUnit('EUR')
												)
											: NOT_APPLICABLE}
								</Text>
							</Tooltip>
						</Box>
						<Box className={classes.row}>
							<Text
								className={clsx(
									classes.label,
									IS_SMALL && classes.labelSm,
									IS_EXTRA_SMALL && classes.labelXs
								)}
							>
								{t('global.coverage')}
							</Text>
							<Tooltip
								isActive={props.data.coverage.length > 23}
								label={props.data.coverage}
							>
								<Text
									className={clsx(
										classes.value,
										IS_SMALL && classes.valueSm,
										IS_EXTRA_SMALL && classes.valueXs
									)}
								>
									{formatCurrency(
										Number(props.data.coverage),
										getCurrencyUnit('EUR')
									)}
								</Text>
							</Tooltip>
						</Box>

						<Progress
							radius={'0px'}
							size={IS_SMALL ? 'sm' : 'md'}
							value={props.data.progress}
						/>
					</>
				);

			default:
				break;
		}
	};

	const getButtons = (_quantity: number) => {
		switch (props.type) {
			case 'view-measurability':
				return (
					<Box
						className={
							classes.containerMesurabilityButtons
						}
					>
						<Tooltip
							isActive={
								t('global.moreDetails').length > 18
							}
							label={t('global.moreDetails')}
						>
							<div
								style={{
									width: '100%',
								}}
							>
								<Button
									data-testid="CRDkG199_Z5jdHi6Be-button"
									variant={
										props.isAdded ? 'filled' : 'outline'
									}
									size={
										IS_SMALL || IS_EXTRA_SMALL
											? 'xs'
											: 'md'
									}
									fullWidth
									onClick={props.handleShowMoreDetails}
								>
									{truncateString(
										t('global.moreDetails'),
										18
									)}
								</Button>
							</div>
						</Tooltip>
					</Box>
				);
			case 'view-additionality':
				return (
					<Box
						className={
							classes.containerMesurabilityButtons
						}
					>
						<Tooltip
							isActive={
								t('global.moreDetails').length > 18
							}
							label={t('global.moreDetails')}
						>
							<div
								style={{
									width: '100%',
								}}
							>
								<Button
									data-testid="CRDkG199_Z5jdHi6Be-button"
									variant={
										props.isAdded ? 'filled' : 'outline'
									}
									size={
										IS_SMALL || IS_EXTRA_SMALL
											? 'xs'
											: 'md'
									}
									fullWidth
									onClick={props.handleShowMoreDetails}
								>
									{truncateString(
										t('global.moreDetails'),
										18
									)}
								</Button>
							</div>
						</Tooltip>
					</Box>
				);
			case 'measurability':
				return (
					<Box
						className={
							classes.containerMesurabilityButtons
						}
					>
						<Box
							className={clsx(
								classes.containerNumericInput,
								IS_SMALL &&
									classes.containerNumericInputSm,
								IS_EXTRA_SMALL &&
									classes.containerNumericInputXs
							)}
						>
							<NumericInput
								max={parseFloat(
									props.data.tokenAvailable
								)}
								defaultValue={props.tco2}
								size={
									IS_SMALL || IS_EXTRA_SMALL ? 'xs' : 'md'
								}
								unit={'Tco2'}
								variant={'outline'}
								onChange={props.onChangeTco2}
								value={_quantity}
								disabled={
									props.disabled || props.disableActions
								}
							/>
						</Box>
						<Tooltip
							isActive={
								(props.isAdded
									? t('global.added')
									: t('offsets.addToOffset')
								).length > 18
							}
							label={
								props.isAdded
									? t('global.added')
									: t('offsets.addToOffset')
							}
						>
							<div
								style={{
									width: '100%',
								}}
							>
								<Button
									data-testid="CRDkG199_Z5jdHi6B-button"
									disabled={
										props.disabled || props.disableActions
									}
									variant={
										props.isAdded ? 'filled' : 'outline'
									}
									size={
										IS_SMALL || IS_EXTRA_SMALL
											? 'xs'
											: 'md'
									}
									fullWidth
									onClick={() => {
										if (!props.isAdded) props.onAdd(true);
									}}
									readOnly={props.isAdded}
								>
									{truncateString(
										props.isAdded
											? t('global.added')
											: t('offsets.addToOffset'),
										18
									)}
								</Button>
							</div>
						</Tooltip>
					</Box>
				);
			case 'additionality':
				return (
					<Tooltip
						isActive={
							(props.isAdded
								? t('global.added')
								: t('offsets.addToOffset')
							).length > 20
						}
						label={
							props.isAdded
								? t('global.added')
								: t('offsets.addToOffset')
						}
					>
						<div
							style={{
								width: '100%',
							}}
						>
							<Button
								data-testid="CRDkG198_Z5jdHi6B-button"
								disabled={props.disabled}
								variant={
									props.isAdded ? 'filled' : 'outline'
								}
								size={'md'}
								fullWidth
								onClick={() => {
									props.onAdd(true);
								}}
							>
								{truncateString(
									props.isAdded
										? t('global.added')
										: t('offsets.addToOffset'),
									20
								)}
							</Button>
						</div>
					</Tooltip>
				);

			default:
				break;
		}
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Tooltip
			isActive={props.disabled}
			label={props.disabledLabel}
		>
			<Box
				className={clsx(
					classes.container,
					IS_SMALL && classes.containerSm,
					IS_EXTRA_SMALL && classes.containerXs,
					props.disabled && classes.containerDisabled,
					props.inactive && classes.containerDisabled
				)}
				onClick={props.handleShowMoreDetails}
			>
				{/** Header */}
				<BackgroundImage
					src={props.data.image}
					className={classes.containerHeader}
				>
					<Box className={classes.overlay} />
					<Box className={classes.containerAboveOverlay}>
						<Flex justify="space-between">
							<Stack gap={'0px'}>
								<Text
									className={clsx(
										classes.headerSubtitle,
										IS_SMALL && classes.headerSubtitleSm,
										IS_EXTRA_SMALL &&
											classes.headerSubtitleXs
									)}
								>
									{t('global.project')}
								</Text>
								<Tooltip
									isActive={props.data.name.length > 60}
									label={props.data.name}
								>
									<Text
										className={clsx(
											classes.headerTitle,
											IS_SMALL && classes.headerTitleSm,
											IS_EXTRA_SMALL &&
												classes.headerTitleXs
										)}
									>
										{truncateString(props.data.name, 60)}
									</Text>
								</Tooltip>
							</Stack>
							{props.data?.supportedBy && (
								<img
									src={props.data?.supportedBy}
									className={classes.logoOrganization}
								/>
							)}
						</Flex>
					</Box>
					<Group
						className={classes.containerAboveOverlay}
						gap={'5px'}
					>
						<Avatar
							radius={'xl'}
							src={`https://flagcdn.com/w40/${props.data.country?.code.toLowerCase()}.png`}
							size={
								IS_SMALL || IS_EXTRA_SMALL ? 'xs' : 'sm'
							}
						/>

						<Text
							className={clsx(
								classes.headerCountry,
								IS_SMALL && classes.headerCountrySm,
								IS_EXTRA_SMALL && classes.headerCountryXs
							)}
						>
							{props.data.country?.name}
						</Text>
					</Group>
				</BackgroundImage>
				{/** Body */}
				<Box className={classes.containerBody}>
					{getBody()}
				</Box>
				{/** Buttons */}
				<Box className={classes.containerButtons}>
					{getButtons(props.data.quantitytco2eq)}
				</Box>
			</Box>
		</Tooltip>
	);
};
